"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.operatorAgentSpendResponseSchema = exports.operatorAgentSpendRowSchema = exports.operatorRunChoreResponseSchema = exports.operatorRequeueResponseSchema = exports.operatorJobFailureListResponseSchema = exports.operatorJobListResponseSchema = exports.OPERATOR_JOB_STATES = exports.operatorJobSchema = exports.operatorJobFailureSchema = exports.operatorOperationsResponseSchema = exports.operatorJobKindStateSchema = exports.operatorChoreStateSchema = exports.choreRunStatusSchema = exports.operatorAuditQuerySchema = exports.operatorAuditExtractResponseSchema = exports.operatorAuditListResponseSchema = exports.operatorAuditEntrySchema = exports.operatorAuditActorSchema = exports.inviteTenantRequestSchema = exports.inviteOperatorRequestSchema = exports.operatorRosterResponseSchema = exports.operatorRosterEntrySchema = exports.deleteTenantResponseSchema = exports.deleteTenantRequestSchema = exports.operatorActivateRequestSchema = exports.operatorSuspendRequestSchema = exports.operatorTenantListQuerySchema = exports.operatorTenantListResponseSchema = exports.operatorTenantSchema = exports.tenantStatusSchema = exports.setTenantFlagRequestSchema = exports.operatorFlagsResponseSchema = exports.operatorFlagStateSchema = exports.setSeatLimitRequestSchema = exports.assignPlanRequestSchema = exports.updateOperatorPlanRequestSchema = exports.createOperatorPlanRequestSchema = exports.operatorPlanListResponseSchema = exports.operatorPlanSchema = exports.operatorJoinVerifyRequestSchema = exports.operatorJoinBeginRequestSchema = exports.operatorJoinPreviewSchema = exports.operatorEnrolVerifyRequestSchema = exports.operatorEnrolmentSchema = exports.operatorEnrolBeginRequestSchema = exports.operatorSignInResponseSchema = exports.operatorSessionSchema = exports.operatorSchema = exports.operatorPasswordCheckRequestSchema = exports.operatorSignInRequestSchema = void 0;
exports.CONTROL_PANEL_PATHS = exports.OPERATOR_SPEND_WINDOWS = exports.operatorMcpUsageResponseSchema = exports.operatorMcpUsageRowSchema = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const pagination_1 = require("../pagination");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Control Panel's contract. S3, `decisions.md` S-1, S-6, S-7, S-8, S-9. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **DEFINED HERE, IN THE PACKAGE BOTH SIDES IMPORT, LIKE EVERY OTHER CONTRACT** — even though
 * `decisions.md` S-6 gives the Control Panel its own application, its own route table and its own
 * API client. Those are separate for a reason (*"a shared table is the first step toward a shared
 * session"*); a **schema** is the opposite case. The server validates with it and the client infers
 * from it, so a divergence is a type error rather than a 400 nobody expected. The outgoing project
 * had the panel mirroring the server's shapes by hand — `operator_tenantSchema` with the comment
 * *"WP27's shape, mirrored — the server has no DTO for it"* — and that is exactly the drift this
 * package exists to prevent.
 *
 * ⚠️ **What is deliberately absent: anything about a workspace's contents.** S-1 bounds this
 * console to tenancy, and the shapes below are the boundary written down — a tenant, its counts,
 * and its lifecycle. There is no task, no worklog and no member name here, and `0032` makes that
 * true at the database rather than by omission.
 */
/* ── Authentication ─────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **PASSWORD AND CODE IN ONE REQUEST, AND THAT IS THE SECURITY PROPERTY** (`decisions.md`
 * S-8). A two-step flow has to represent *"password accepted, second factor pending"* as something
 * the client holds — and that something is a credential, one debugging session away from being
 * accepted on its own. Here there is nothing between unauthenticated and fully authenticated.
 *
 * ⚠️ **`totpCode` is required, not optional.** An operator with no verified factor cannot sign in
 * at all; there is no grace period and no "enrol later". An operator account reachable with a
 * password alone is the account an attacker goes for, and it holds cross-tenant read.
 */
exports.operatorSignInRequestSchema = zod_1.z.object({
    email: zod_1.z.email().min(3).max(320),
    password: zod_1.z.string().min(1),
    /* Six digits. GoTrue's own TOTP is 6, and a bound here means a 2000-character "code" is refused
     * before it reaches the mailer-shaped part of the flow. */
    totpCode: zod_1.z.string().regex(/^\d{6}$/),
});
/**
 * 2 Nov 2026 · **The password step's check — it answers, and it issues nothing.** The screen asked
 * for the code before anything had looked at the password, so a mistyped password was found out
 * only after the code had been typed too. This confirms the password (and that the address is an
 * active operator with a factor) before the code step appears. ⚠️ It returns no token and no
 * state: the sign-in that follows still carries address, password and code in one request, which
 * is what S-8 protects.
 */
exports.operatorPasswordCheckRequestSchema = exports.operatorSignInRequestSchema.pick({
    email: true,
    password: true,
});
/**
 * The operator, as the console needs them. No `totp_factor_id`: it is not a secret, and it is also
 * not the client's business — the client asks the server to enrol and to verify.
 */
exports.operatorSchema = zod_1.z.object({
    id: zod_1.z.string(),
    email: zod_1.z.string(),
    name: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **The token is returned in the BODY, not set as a cookie, and the asymmetry with the product
 * is deliberate.** The product's refresh token is an httpOnly cookie because the browser must never
 * hold it (`hard-won.md` WP8i-1: a browser-held refresh token makes per-session revocation
 * unenforceable by construction). There is nothing to refresh here — an opaque 8-hour token has no
 * renewal path — so the cookie would buy nothing and cost the CSRF surface a cookie brings. The
 * console holds it in `sessionStorage` and sends it as a bearer.
 */
exports.operatorSessionSchema = zod_1.z.object({
    token: zod_1.z.string(),
    expiresAt: zod_1.z.string(),
});
exports.operatorSignInResponseSchema = zod_1.z.object({
    session: exports.operatorSessionSchema,
    operator: exports.operatorSchema,
});
/* ── Enrolment ──────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **Enrolment takes the password too, because there is no session to authorise it.** The
 * operator exists (created by `scripts/superadmin.mjs`) and cannot sign in until a factor is
 * verified — so the only thing that can prove who is asking is the password itself. This is the one
 * route where that is true, and it is why enrolment is a *route* rather than a screen inside the
 * console.
 */
exports.operatorEnrolBeginRequestSchema = zod_1.z.object({
    email: zod_1.z.email().min(3).max(320),
    password: zod_1.z.string().min(1),
});
/**
 * What an authenticator app needs. Returned **once**, and never readable again — the secret lives
 * in GoTrue and this is the only moment it crosses the wire.
 */
exports.operatorEnrolmentSchema = zod_1.z.object({
    factorId: zod_1.z.string(),
    /* The `otpauth://` URI. The console renders it as text beside the QR code, because an operator
     * pasting it into a password manager is a real workflow. */
    uri: zod_1.z.string(),
    secret: zod_1.z.string(),
});
exports.operatorEnrolVerifyRequestSchema = zod_1.z.object({
    email: zod_1.z.email().min(3).max(320),
    password: zod_1.z.string().min(1),
    factorId: zod_1.z.string().min(1),
    totpCode: zod_1.z.string().regex(/^\d{6}$/),
});
/* ── Joining from an invitation (0179) ──────────────────────────────────────────────────────── */
/**
 * What the invitation link shows before anything is typed: whose invitation it is.
 *
 * ⚠️ **Only for a usable link.** A dead one — used, revoked, expired, or for an operator removed
 * since — is one `not_found`, because the console says the same sentence for all of them.
 */
exports.operatorJoinPreviewSchema = zod_1.z.object({
    email: zod_1.z.string(),
    name: zod_1.z.string().nullable(),
});
/**
 * Step one: the password. Answers with the key to scan — `operatorEnrolmentSchema`, the same
 * shape `/enrol` answers with, because it is the same factor.
 *
 * ⚠️ The policy is `checkPassword`'s, enforced on the server too: the admin endpoint that sets it
 * does not apply GoTrue's own (`gotrue.ts`, `setPasswordForUser`).
 */
exports.operatorJoinBeginRequestSchema = zod_1.z.object({
    password: zod_1.z.string().min(1).max(200),
});
/**
 * Step two: the first code. Answers with a session — `operatorSignInResponseSchema` — because a
 * verified password and a verified code are exactly what signing in proves, and making somebody
 * type a second code to reach the console they just finished joining would be a step for its own
 * sake.
 */
exports.operatorJoinVerifyRequestSchema = zod_1.z.object({
    password: zod_1.z.string().min(1).max(200),
    factorId: zod_1.z.string().min(1),
    totpCode: zod_1.z.string().regex(/^\d{6}$/),
});
/* ── Plans (S6) ─────────────────────────────────────────────────────────────────────────────── */
/**
 * One plan in the catalogue.
 *
 * ⚠️ **`seatLimit` is `null` for unlimited**, and the console renders that word rather than a blank:
 * an empty cell in a limit column reads as "not loaded". `0034` argues why null is a real value.
 *
 * ⚠️ **`featureFlags` carries only keys the registry knows.** The service refuses an unregistered
 * key on write, so a plan cannot accumulate opinions about features that no longer exist — the exact
 * rot the predecessor's `feature_flags` TABLE was built to hold.
 *
 * ⚠️ **`seatPrice` is a STRING, like every other money value on this wire.** `numeric(14,2)` does not
 * survive a JavaScript number, and the driver hands it over as text for that reason. Formatting is
 * the client's; arithmetic on it is nobody's.
 */
exports.operatorPlanSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `plan_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('plan'),
    name: zod_1.z.string(),
    seatPrice: zod_1.z.string(),
    currency: zod_1.z.string(),
    seatLimit: zod_1.z.number().nullable(),
    featureFlags: zod_1.z.record(zod_1.z.string(), zod_1.z.boolean()),
    isActive: zod_1.z.boolean(),
    /** How many tenants are on it. The console needs it to say what deactivating would affect. */
    tenantCount: zod_1.z.number(),
    version: zod_1.z.number(),
});
exports.operatorPlanListResponseSchema = zod_1.z.object({ data: zod_1.z.array(exports.operatorPlanSchema) });
/**
 * ⚠️ **`currency` is three upper-case letters and nothing more is checked**, because there is no
 * `currencies` table in this schema to check against — Phase 2 owns that reference data, and
 * `debt.md` records the gap. A closed list here would be inventing one.
 */
exports.createOperatorPlanRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(100),
    /* A string on the way in as well as out, so the client never rounds a price on its way to a
     * `numeric`. Two decimal places at most, which is what the column stores. */
    seatPrice: zod_1.z.string().regex(/^\d{1,12}(\.\d{1,2})?$/),
    currency: zod_1.z.string().regex(/^[A-Z]{3}$/),
    seatLimit: zod_1.z.number().int().min(0).nullable(),
    featureFlags: zod_1.z.record(zod_1.z.string(), zod_1.z.boolean()),
});
/**
 * ⚠️ **`version` is required and everything else is optional**, so a rename and a repricing are the
 * same endpoint without either having to send the other's fields. A stale write is 409, as
 * everywhere else in this product.
 */
exports.updateOperatorPlanRequestSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1).max(100).optional(),
    seatPrice: zod_1.z
        .string()
        .regex(/^\d{1,12}(\.\d{1,2})?$/)
        .optional(),
    currency: zod_1.z
        .string()
        .regex(/^[A-Z]{3}$/)
        .optional(),
    seatLimit: zod_1.z.number().int().min(0).nullable().optional(),
    featureFlags: zod_1.z.record(zod_1.z.string(), zod_1.z.boolean()).optional(),
    isActive: zod_1.z.boolean().optional(),
    version: zod_1.z.number().int().nonnegative(),
});
/**
 * ⚠️ **Nullable, because unassigning is the same act as assigning.** A separate `DELETE` for
 * "no plan" would be two endpoints for one field, and the trial state — no plan at all — has to be
 * reachable again after a mistake.
 */
exports.assignPlanRequestSchema = zod_1.z.object({
    planId: zod_1.z.uuid().nullable(),
});
/**
 * The per-tenant seat override. `null` returns the tenant to its plan's allowance.
 *
 * ⚠️ Lowering it below current usage is permitted and removes nobody — `seat.service.ts` argues why
 * a limit that could revoke access would turn a support action into an outage.
 */
exports.setSeatLimitRequestSchema = zod_1.z.object({
    seatLimit: zod_1.z.number().int().min(0).nullable(),
});
/* ── Feature flags (S6) ─────────────────────────────────────────────────────────────────────── */
/**
 * One flag as the console shows it: what it is, what the tenant gets, and **why**.
 *
 * ⚠️⚠️ **`source` IS NOT DECORATION.** A toggle showing `on` without saying whether that came from
 * the plan, from an override or from the shipped default is the thing an operator misreads at two in
 * the morning — and the three cases need different actions: change the plan, clear the override, or
 * nothing. `default` also tells them there is no row to delete.
 */
exports.operatorFlagStateSchema = zod_1.z.object({
    key: zod_1.z.string(),
    description: zod_1.z.string(),
    enabled: zod_1.z.boolean(),
    source: zod_1.z.enum(['override', 'plan', 'default']),
    /** What the tenant would get with the override cleared. Rendered beside the Clear control. */
    withoutOverride: zod_1.z.boolean(),
});
exports.operatorFlagsResponseSchema = zod_1.z.object({ data: zod_1.z.array(exports.operatorFlagStateSchema) });
exports.setTenantFlagRequestSchema = zod_1.z.object({ enabled: zod_1.z.boolean() });
/** `YYYY-MM-DD`, the same local schema `contracts/contract.ts` declares — a calendar day on the wire. */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/* ── Tenants ────────────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️⚠️ **`awaiting_signup` MEANS NOBODY HAS ACCEPTED YET, AND IT IS A REAL TENANT WHILE IT
 * WAITS.** `0172` adds the label and `decisions.md` S-16 licensed it: an operator invites an
 * organisation by naming it and naming an admin, and the row exists from that moment — so it can
 * be listed, suspended, given a plan and audited before anybody has signed in. What it has is a
 * workspace and no members.
 *
 * ⚠️ **The product never sees it.** Every consumer there branches on `suspended` alone, and
 * `memberships_for_user` returns nothing for a tenant with no active member, which is exactly what
 * this state is.
 */
exports.tenantStatusSchema = zod_1.z.enum(['trial', 'active', 'suspended', 'awaiting_signup']);
/**
 * One tenant, as the console shows it.
 *
 * ⚠️ **`workspaceCount` and `memberCount` are COUNTS, and that is the S-1 line drawn precisely.**
 * How large a tenant is, is the question an operator console exists to answer; who is in it is a
 * workspace's contents. `0032`'s `tenant_counts` is a definer function returning two integers with
 * no parameter that could widen it, because the operator role has — correctly — no grant on
 * `members`.
 *
 * ⚠️ **No `name` for the workspaces, for the same reason there is no member name.** The count says
 * how many; a list of names would be a directory of a customer's internal structure.
 */
exports.operatorTenantSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** `tenant_e9111099` — the public identifier `0194` mints beside the uuid; see `identifier.ts`. */
    identifier: (0, identifier_1.identifierSchema)('tenant'),
    name: zod_1.z.string(),
    slug: zod_1.z.string(),
    status: exports.tenantStatusSchema,
    trialEndsAt: zod_1.z.string().nullable(),
    suspendedAt: zod_1.z.string().nullable(),
    /* The operator's own note. Present here because the console is its only audience — the tenant
     * never sees it (0033). */
    suspensionReason: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    workspaceCount: zod_1.z.number(),
    memberCount: zod_1.z.number(),
    /* ── S6 ──────────────────────────────────────────────────────────────────────────────────── */
    /**
     * ⚠️ **The plan, flattened to what the list needs**, not the whole `operatorPlanSchema`. A tenant
     * row does not need a plan's `version` or its flag mapping, and embedding the full object would
     * make every list response carry the catalogue N times.
     */
    planId: zod_1.z.string().nullable(),
    planName: zod_1.z.string().nullable(),
    /** Seats used and reserved, per BRS §3.11.2's *"seat count"* column. */
    seatsUsed: zod_1.z.number(),
    seatsPending: zod_1.z.number(),
    /**
     * ⚠️⚠️ **WHEN A MEMBER OF THIS TENANT LAST HELD A LIVE SESSION, NOT WHEN THE API LAST
     * SERVED THEM.** Derived from `user_sessions.last_seen_at` through `tenant_counts` — `0167`
     * says why a recorded column was refused: it would be a write on the hot path of every call
     * the product serves, for a figure five people read in a week.
     *
     * ⚠️ **`null` means nobody ever has**, which is a real state for a trial and renders as
     * *never* rather than as a very old date.
     */
    lastRequestAt: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **THE PERSON WHO SIGNED THE ORGANISATION UP — ITS EARLIEST MEMBERSHIP, DERIVED.**
     * `0168` says why there is no column: two on `tenants` would mean a backfill, a write in
     * provisioning, and a value that goes stale the day somebody's name changes, to store what
     * the earliest `members` row already says.
     *
     * ⚠️⚠️ **THIS IS THE ONE PERSON AN OPERATOR MAY SEE, AND IT IS NOT A CRACK IN S-1.** It
     * crosses the boundary through `tenant_counts`, whose projection is the boundary — three
     * scalars about one person, never a list and never a query. S-16 refused the Users tab on
     * exactly this ground; a contact is a property of the organisation's record, which is what
     * an operator administers.
     *
     * ⚠️ All three are `null` together, for a tenant whose only member has been deleted.
     */
    contactName: zod_1.z.string().nullable(),
    contactEmail: zod_1.z.string().nullable(),
    contactSince: zod_1.z.string().nullable(),
    /** ⚠️ `0175` · when the contact last held a session. `null` for somebody who never has. */
    contactLastSeenAt: zod_1.z.string().nullable(),
    /**
     * ⚠️⚠️ **THE PENDING INVITATION, AND IT IS THE ONLY ADDRESS AN OPERATOR EVER SEES FROM
     * INSIDE A WORKSPACE.** `workspace_invitations` carries a `workspace_id`, so S-7 gives the
     * operator role no grant on it; these two come through `tenant_counts`, whose projection is the
     * boundary — one address and one timestamp about one pending invitation, never a list.
     *
     * ⚠️ **Both are `null` unless the tenant is `awaiting_signup`** — `0176`. An established
     * customer's pending colleague invitations are workspace business, not the organisation's
     * invitation; `0173` returned them too and an eleven-person tenant rendered as still waiting for
     * its first sign-up. So an organisation has one or the other of this pair and the contact, never
     * both.
     */
    invitedEmail: zod_1.z.string().nullable(),
    /** `null` once the link was used — an accepted invitation has nothing left to expire. */
    invitedExpiresAt: zod_1.z.string().nullable(),
    /**
     * ⚠️ `0202` · **"Awaiting setup"**: when the invited admin accepted, while the organisation still
     * has no workspace. The admin has an account and closed the tab before setup step 2; the console
     * offers a resend, whose link sends them to sign in and resume. `null` in every other state.
     */
    setupPendingSince: zod_1.z.string().nullable(),
    /** The effective allowance: the tenant's override, else the plan's, else `null` for unlimited. */
    seatLimit: zod_1.z.number().nullable(),
    /** Set only when the tenant has its own override, so the console can show it as an override. */
    seatLimitOverride: zod_1.z.number().nullable(),
    /**
     * ⚠️⚠️ **`null` FOR EVERY TENANT THAT IS NOT `active`, AND THAT IS THE R-5 LINE HELD ONE LEVEL
     * UP.** BRS §3.11.2 asks for MRR in the tenant list. Seats × seat price is a real number for a
     * paying customer; for a trial it is a number nobody owes, and for a suspended tenant it is a
     * number nobody is billing. C16 refused to render a fabricated invoice history for exactly this
     * reason, and a revenue figure against a tenant that pays nothing is the same species of
     * placeholder — *"the one kind that reads as fact"*.
     *
     * A string, like every other money value here.
     */
    monthlyValue: zod_1.z.string().nullable(),
});
exports.operatorTenantListResponseSchema = (0, pagination_1.listResponseSchema)(exports.operatorTenantSchema).extend({
    /**
     * ⚠️⚠️ **HOW MANY EACH STATUS WOULD LEAVE, UNDER EVERY OTHER FILTER BUT NOT THIS ONE.** The
     * redesign's status dropdown prints a count beside each choice — *Aktív 9, Felfüggesztve 2* — and
     * the count that helps is the one for *switching to it*, which is why the status filter is the
     * one left out. Counted in SQL; a browser counting a page would count the page.
     *
     * ⚠️ Optional, so a response from before this field still parses; the dropdown then shows no
     * counts rather than zeroes, because zero is a claim.
     */
    counts: zod_1.z.record(zod_1.z.string(), zod_1.z.number()).optional(),
});
exports.operatorTenantListQuerySchema = zod_1.z.object({
    cursor: pagination_1.cursorSchema.optional(),
    /** ⚠️ An alternative to `cursor`, not a companion — the API's `page.ts` says which wins. */
    offset: pagination_1.offsetSchema.optional(),
    /**
     * ⚠️ **Filtering and searching are SERVER-side**, per CLAUDE.md §3 — *"applied before permission
     * trimming, never fetch-then-filter-in-memory"*. A console that filtered a page in the browser
     * would show "no suspended tenants" whenever they happened to be on page two.
     */
    status: exports.tenantStatusSchema.optional(),
    /* Matches name or slug. Short bound: this is a lookup, not a full-text search. */
    q: zod_1.z.string().trim().min(1).max(100).optional(),
    /**
     * ⚠️ **Created on or after / on or before, as CALENDAR DATES, inclusive at both ends.** The
     * redesign's *Létrehozva ettől – eddig*. A date, not an instant: *"created on 12 March"* is a day,
     * and `hard-won.md` is blunt that one converter cannot serve both. Compared against the UTC day of
     * `created_at`, which is the only day the server can name without knowing whose calendar it is.
     */
    createdFrom: wireDateSchema.optional(),
    createdTo: wireDateSchema.optional(),
});
/**
 * ⚠️⚠️ **THE REASON IS MANDATORY, AND IT IS FREE TEXT RATHER THAN A DROPDOWN.** The phase plan
 * makes the same call for impersonation (*"a mandatory free text, not a dropdown"*) and the
 * argument carries: a category tells a colleague six months later almost nothing, and the audit row
 * is the whole control (`decisions.md` R-15), so it has to be worth reading. `0033` bounds it at
 * 2000 characters, which is the length of an explanation rather than a label.
 *
 * ⚠️ It is never shown to the tenant. See `suspended-screen.tsx`.
 */
exports.operatorSuspendRequestSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(1).max(2000),
});
/**
 * ⚠️ **Activation takes no reason, and the asymmetry is on purpose.** Suspending removes somebody's
 * access and is the act that needs explaining; restoring it is the default state of a customer. The
 * audit row still records who did it and when.
 */
exports.operatorActivateRequestSchema = zod_1.z.object({});
/**
 * ┌───────────────────────────────────────────────────────────────────┐
 * │ Deleting an organisation for good. Three proofs, and each proves something else. │
 * └───────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THERE IS NO SUSPEND-FIRST GATE (RULED 24 Sep), SO THE CONFIRMATION IS THE ONLY GUARD.**
 * A live, paying customer can be deleted in one sitting, and the three fields below are what stands
 * between a click and that. They are deliberately not three ways of asking the same question:
 *
 *  * `slug` proves you are looking at the right organisation. It is also the weakest of the three,
 *    because a slug can be copied off the screen — which is exactly why it is not alone;
 *  * `reason` goes into the audit log, which outlives the organisation. It is the only part a
 *    person reading this in six months will have;
 *
 * ⚠️⚠️ **A THIRD PROOF — A FRESH TOTP CODE — IS DRAWN BY THE REDESIGN AND IS NOT BUILT, ON
 * PURPOSE.** It would prove the person at the keyboard is still the operator who signed in, which
 * is the right threat (a console left open on an unlocked laptop). It is not here because every
 * way of checking it today is worse than not checking it: `verifyTotpFactor` needs a GoTrue access
 * token the operator session deliberately does not hold, keeping one would widen what a stolen
 * session token grants, and re-running `signIn` would mint a second session and put a phantom
 * device in the operator's own list. The fix is a verify-only method that mints nothing, and it is
 * its own piece of work rather than a line in this one.
 */
exports.deleteTenantRequestSchema = zod_1.z.object({
    /** The tenant's own slug, typed. Compared server-side; a mismatch is `validation_failed`. */
    slug: zod_1.z.string().min(1),
    /** ⚠️ At least ten characters, because "cleanup" is not a reason anybody can act on later. */
    reason: zod_1.z.string().trim().min(10).max(500),
});
/** What the console shows afterwards, when the organisation it was looking at no longer exists. */
exports.deleteTenantResponseSchema = zod_1.z.object({
    name: zod_1.z.string(),
    slug: zod_1.z.string(),
    deletedAt: zod_1.z.string(),
    /** ⚠️ The audit entry's id, so the confirmation screen can link to the record that survives. */
    auditId: zod_1.z.string(),
});
/**
 * ┌───────────────────────────────────────────────────────────────────┐
 * │ C8 · The operator roster. The console's own principal set.        │
 * └───────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SHAPE IS SHORT, AND `0170` EXPLAINS WHAT IS MISSING FROM IT.** `superadmins` holds a
 * `totp_factor_id`; this carries `enrolled`, a boolean. GoTrue's identifier for somebody's second
 * factor has no use on a screen beyond *have they set one up*, and a field nobody needs is a field
 * a later screen renders.
 *
 * ⚠️ **Three states, and they come from two columns.** *Suspended* is `status`; *invited* is an
 * active operator who has not enrolled yet and therefore cannot sign in at all; *active* is the
 * rest. The client derives it rather than the server inventing a fourth enum — CLAUDE.md rule 8.
 */
exports.operatorRosterEntrySchema = zod_1.z.object({
    id: zod_1.z.string(),
    email: zod_1.z.string(),
    name: zod_1.z.string().nullable(),
    status: zod_1.z.enum(['active', 'disabled']),
    /** ⚠️ `false` means they cannot sign in: a verified factor is mandatory with no grace period. */
    enrolled: zod_1.z.boolean(),
    /**
     * ⚠️ `0177` · when the factor was verified. `null` for anybody not enrolled AND for a factor
     * enrolled before the column existed — nothing recorded it, and a guessed date would be worse
     * than none.
     */
    enrolledAt: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    /** ⚠️ `null` for somebody who has never signed in — a real state for an invitation. */
    lastSeenAt: zod_1.z.string().nullable(),
    /** ⚠️ True on exactly one row: the operator reading the screen, who cannot act on themselves. */
    isYou: zod_1.z.boolean(),
});
exports.operatorRosterResponseSchema = zod_1.z.object({ data: zod_1.z.array(exports.operatorRosterEntrySchema) });
/**
 * ⚠️⚠️ **AN INVITATION CARRIES NO PASSWORD, AND THE OMISSION IS THE DESIGN.** The server makes a
 * GoTrue identity with a random secret nobody ever sees and mails a reset link; the invited person
 * chooses their own password and then enrols an authenticator at `/enrol`. The alternative —
 * generating a password and showing it to the inviter — is a credential that travels through a
 * screenshot, a chat window and somebody's clipboard, which is exactly what `scripts/superadmin.mjs`
 * can get away with on a loopback shell and a console cannot.
 */
exports.inviteOperatorRequestSchema = zod_1.z.object({
    email: zod_1.z.email().trim().min(3).max(320),
    /** Optional, because an address is enough to send an invitation and a name can be set later. */
    name: zod_1.z.string().trim().min(1).max(200).optional(),
});
/**
 * ┌────────────────────────────────────────────────────────────┐
 * │ C10 · An organisation is INVITED, not created.                       │
 * └────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE OPERATOR NAMES THE ORGANISATION, AND THAT IS WHAT MAKES THE ROW CREATABLE.**
 * `tenants.name` is `not null` and `tenants.slug` is `not null unique` against a strict regex — so
 * a row created before anybody had named the organisation would need a fabricated name and a
 * fabricated globally unique slug, then a rename **and a re-slug** on acceptance, where the slug is
 * what addresses are built from. With the name here, the row is correct the first time.
 *
 * ⚠️ **This inverts `POST /tenants`.** Self-serve sign-up creates the tenant from the account; this
 * creates the account's invitation from the tenant. Both end at the same place — `0173` reuses
 * `0027`'s acceptance path rather than growing a second invitation system — and the difference is
 * only who decided the organisation should exist.
 *
 * ⚠️ **No workspace name.** The first workspace is named after the organisation, which the admin
 * can change in settings. The alternative was asking the operator to name a workspace inside a
 * company they do not work at.
 */
exports.inviteTenantRequestSchema = zod_1.z.object({
    /** ⚠️ `0003`'s own bound. A name this cannot slug is refused with `unsluggable_name`. */
    name: zod_1.z.string().trim().min(1).max(200),
    adminEmail: zod_1.z.email().trim().max(320),
    /**
     * ⚠️ **Required, unlike an operator invitation's optional name**, because this one is written
     * into an email somebody who has never heard of us is going to read. `members.display_name` is
     * `not null` and the admin supplies their own on acceptance; this is what the invitation is
     * addressed to.
     */
    adminName: zod_1.z.string().trim().min(1).max(200),
    /**
     * ⚠️ **The plan the organisation starts on, chosen in the invitation** — the redesign's dialog
     * asks for it beside the admin. Optional on the wire so an invitation without one is still
     * possible (no plan means no cap, which `tenant.plan.noneHint` says out loud); the console's
     * dialog always sends one.
     */
    planId: zod_1.z.uuid().optional(),
    /** ⚠️ A seat override from day one; absent follows the plan. `0` means nobody may be added. */
    seatLimit: zod_1.z.number().int().min(0).max(100_000).optional(),
});
/* ── The audit trail. S7. ───────────────────────────────────────────────────────────────────── */
/**
 * One row of `operator_audit_log`, as the console reads it.
 *
 * ⚠️⚠️ **THE ACTOR IS A UNION OF TWO SHAPES, NOT A NULLABLE NAME.** 0033 gave the log an
 * `operator_actor` enum precisely because *"no actor" and "forgot the actor" would look identical* —
 * and a wire shape with `actorName: string | null` reintroduces that ambiguity on the client, where
 * the screen would have to guess whether a blank means the clock or a bug. So `actor` carries the
 * discriminator and the two id fields agree with it.
 */
exports.operatorAuditActorSchema = zod_1.z.enum(['superadmin', 'system']);
exports.operatorAuditEntrySchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    actor: exports.operatorAuditActorSchema,
    /** `null` exactly when `actor` is `system` — 0033's check constraint makes that true. */
    actorId: zod_1.z.uuid().nullable(),
    actorName: zod_1.z.string().nullable(),
    actorEmail: zod_1.z.string().nullable(),
    /**
     * `resource.action` — `tenant.suspend`, `plan.create`, `chore.run`. Not an enum here, and that is
     * deliberate: the console renders a translated label per action and falls back to the raw string
     * for one it does not know, so a new action type from a newer API is legible rather than blank.
     * An enum would make the *client* the thing that has to be deployed first.
     */
    action: zod_1.z.string(),
    resourceType: zod_1.z.string(),
    resourceId: zod_1.z.uuid().nullable(),
    /**
     * The diff, as it was written. Unknown shape by construction — every service writes the columns
     * it changed — so the console renders it generically rather than per action.
     *
     * ⚠️ `z.unknown()` and not `z.record(...)`: a diff can be a scalar-valued object today and grow a
     * nested one tomorrow, and a schema that refused the second would fail the *list* rather than the
     * one row. S6-3's lesson: a fixture that fails the response schema takes a whole screen with it.
     */
    before: zod_1.z.unknown().nullable(),
    after: zod_1.z.unknown().nullable(),
    requestId: zod_1.z.uuid(),
    createdAt: zod_1.z.string(),
});
exports.operatorAuditListResponseSchema = (0, pagination_1.listResponseSchema)(exports.operatorAuditEntrySchema);
/**
 * BRS §3.11.2's *"audit trail extract"*, which is **not** a paged list.
 *
 * ⚠️ **A bare `{ data }` and no `meta`, deliberately.** `pagination.ts` records which lists earned
 * the envelope and why: the extract is deliberately short, has no cursor, and links to the full log
 * instead of paging in place. Reusing the paged schema would have made the client validate against a
 * `next_cursor` the server never sends — and S6-3 is the lesson about what a failed response schema
 * looks like on screen: the error state under a present marker, with every assertion still green.
 */
exports.operatorAuditExtractResponseSchema = zod_1.z.object({
    data: zod_1.z.array(exports.operatorAuditEntrySchema),
});
/**
 * ⚠️ **`resourceType` + `resourceId` rather than `tenantId`.** BRS §3.11.2's *"audit trail extract"*
 * on tenant detail is one instance of a general filter, and the log's own key is the pair — a plan
 * write is `('plan', <id>)`, and a `tenantId` parameter could not express it. One filter, two
 * screens.
 */
exports.operatorAuditQuerySchema = zod_1.z.object({
    cursor: pagination_1.cursorSchema.optional(),
    /** ⚠️ An alternative to `cursor`, not a companion — the API's `page.ts` says which wins. */
    offset: pagination_1.offsetSchema.optional(),
    resourceType: zod_1.z
        .string()
        .trim()
        .regex(/^[a-z_]+$/)
        .max(40)
        .optional(),
    resourceId: zod_1.z.uuid().optional(),
    action: zod_1.z
        .string()
        .trim()
        .regex(/^[a-z_]+\.[a-z_]+$/)
        .max(80)
        .optional(),
});
/* ── System operations: the job monitor. S7, BRS §3.11.7. ───────────────────────────────────── */
exports.choreRunStatusSchema = zod_1.z.enum(['running', 'succeeded', 'failed']);
/**
 * One chore, as the monitor draws it.
 *
 * ⚠️⚠️ **EVERY FIELD BUT THE FIRST THREE IS NULLABLE, BECAUSE "NEVER RAN" IS A STATE.** The list
 * comes from the registry and the ledger fills it in, so a chore that has never run is a row saying
 * so rather than an absence — which is the whole reason the registry is code (`decisions.md` S-14).
 * A schema that required `lastStartedAt` would have forced the server to either omit those chores or
 * invent a timestamp for them.
 */
exports.operatorChoreStateSchema = zod_1.z.object({
    key: zod_1.z.string(),
    /** From the registry, not the database. Operator-facing English, untranslated. */
    description: zod_1.z.string(),
    everyMs: zod_1.z.number().int().positive(),
    lastStartedAt: zod_1.z.string().nullable(),
    lastFinishedAt: zod_1.z.string().nullable(),
    lastStatus: exports.choreRunStatusSchema.nullable(),
    lastTriggeredBy: zod_1.z.enum(['schedule', 'manual']).nullable(),
    /** Whatever the chore returned. `{ "mailed": 2, "tasks": 7 }`. */
    lastDetail: zod_1.z.unknown().nullable(),
    lastError: zod_1.z.string().nullable(),
    runs24h: zod_1.z.number().int().nonnegative(),
    failures24h: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠️ **Computed on the SERVER from `everyMs` and `lastStartedAt`, not in the browser.** A console
     * comparing a timestamp against `Date.now()` on the client is comparing against the operator's
     * laptop clock, which is the one clock nobody controls. `state-rules.md` keeps derived facts on
     * the side that has the real one.
     */
    overdue: zod_1.z.boolean(),
});
/** One queue kind, rolled up. */
exports.operatorJobKindStateSchema = zod_1.z.object({
    kind: zod_1.z.string(),
    /**
     * ⚠️ **Whether anything would actually RUN a job of this kind.** `JOB_KINDS` has three entries
     * and `jobs.module.ts` registers one handler; 0024's worker *fails* a job whose kind has no
     * handler, with `no handler for job kind "x"` — so an operator about to requeue one needs telling
     * that it will fail again immediately. The alternative is a button that looks like it works.
     */
    handled: zod_1.z.boolean(),
    pending: zod_1.z.number().int().nonnegative(),
    running: zod_1.z.number().int().nonnegative(),
    done: zod_1.z.number().int().nonnegative(),
    failed: zod_1.z.number().int().nonnegative(),
    oldestPendingAt: zod_1.z.string().nullable(),
    lastCompletedAt: zod_1.z.string().nullable(),
    lastFailedAt: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **Both rollups in ONE response, because they are one screen.** Two endpoints would mean two
 * loading states and two error states on a page whose whole job is to be glanced at — and
 * `state-rules.md` treats a screen that is half-loaded as a screen that is loading.
 */
exports.operatorOperationsResponseSchema = zod_1.z.object({
    chores: zod_1.z.array(exports.operatorChoreStateSchema),
    jobs: zod_1.z.array(exports.operatorJobKindStateSchema),
});
/**
 * One failed or stalled job.
 *
 * ⚠️⚠️ **THERE IS NO `payload` FIELD, AND THAT ABSENCE IS THE S-1 BOUNDARY ON THE WIRE.** A
 * `mail.send` payload is a rendered email — recipient, subject, body — which is a workspace's
 * contents. 0035's `operator_job_failures` never projects it, so the console cannot ask; this schema
 * is the same fact stated where a reviewer of the client will see it.
 *
 * ⚠️ **`lastError` IS here**, deliberately: it is written by our own code and it is the operator's
 * only diagnostic. `debt.md` carries the row about a future handler putting tenant text in one.
 */
exports.operatorJobFailureSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    kind: zod_1.z.string(),
    status: zod_1.z.enum(['pending', 'running', 'done', 'failed']),
    attempts: zod_1.z.number().int().nonnegative(),
    maxAttempts: zod_1.z.number().int().positive(),
    lastError: zod_1.z.string().nullable(),
    runAfter: zod_1.z.string(),
    lockedAt: zod_1.z.string().nullable(),
    lockedBy: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    completedAt: zod_1.z.string().nullable(),
    /**
     * ⚠️ **All four are nullable because a job need not belong to a workspace.**
     * `enqueue_system_mail` (0028) exists for the password-reset mail that has none, and those are
     * precisely the failures no tenant is around to notice.
     */
    workspaceId: zod_1.z.uuid().nullable(),
    workspaceName: zod_1.z.string().nullable(),
    tenantId: zod_1.z.uuid().nullable(),
    tenantName: zod_1.z.string().nullable(),
});
/**
 * ⚠️ `0178` · the job list by any state — the redesign's *Háttérfeladatok* with its state filter.
 * The same payload-free projection as a failure (S-15), plus whether a `running` job is in fact
 * stalled, which is what makes it count as broken.
 */
exports.operatorJobSchema = exports.operatorJobFailureSchema.extend({ stalled: zod_1.z.boolean() });
exports.OPERATOR_JOB_STATES = ['failed', 'pending', 'running', 'done'];
exports.operatorJobListResponseSchema = (0, pagination_1.listResponseSchema)(exports.operatorJobSchema).extend({
    /** Per state, unfiltered — the numbers beside the filter's choices. */
    counts: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
});
exports.operatorJobFailureListResponseSchema = (0, pagination_1.listResponseSchema)(exports.operatorJobFailureSchema);
/**
 * What a requeue answers with.
 *
 * ⚠️ `attemptsBefore` is the tries that were SPENT, which is why 0035's function needed two CTEs —
 * `RETURNING` on an UPDATE reads the new row, so the obvious version reported the zero it had just
 * written. The console shows it because *"5 attempts discarded, queued again"* is a different
 * sentence from *"queued again"*.
 */
exports.operatorRequeueResponseSchema = zod_1.z.object({
    id: zod_1.z.uuid(),
    kind: zod_1.z.string(),
    attemptsBefore: zod_1.z.number().int().nonnegative(),
});
/**
 * ⚠️⚠️ **A CHORE RUN TAKES NO BODY, AND THE REASON IS THE AUDIT ROW.** A `reason` field would be the
 * natural thing to copy from suspension — but suspension takes one because it removes somebody's
 * access and the reason is the whole control (R-15). Running an idempotent chore an hour early
 * removes nothing, and a mandatory field nobody can say anything useful in is a field people type
 * "test" into, which makes every real reason less believable.
 */
exports.operatorRunChoreResponseSchema = zod_1.z.object({
    key: zod_1.z.string(),
    /** ⚠️ `true` means it RAN, not that it succeeded. The ledger row carries the outcome. */
    started: zod_1.z.boolean(),
});
/* ── The addresses ──────────────────────────────────────────────────────────────────────────── */
/**
 * ⚠️ **`/control-panel/*`, and NOT under `/auth` or `/tenants`.** Two reasons: the prefix is what a
 * deployment can route or firewall as a unit — the phase plan's §0.13 puts the console on its own
 * deployment — and `/tenants` already means *"provision mine"* to a signed-in product user
 * (`POST /tenants`, S1). One prefix for a different principal set, so nothing has to be told apart
 * by verb.
 *
 * ⚠️ **Named once, here, for `auth-paths.ts`'s reason.** WP34-2 exists because a constant shared
 * between four consumers does not help when one of them discards the value; a path spelled in the
 * controller and again in the client is the same failure with fewer steps.
 */
/* ── C27.5 · what the agent has cost ─────────────────────────────────────────────────────────── */
/**
 * One workspace's agent spend, inside a tenant.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THERE IS NO FIELD HERE THAT COULD HOLD A TRANSCRIPT, AND THAT IS THE  │
 * │ WHOLE SHAPE OF THIS SCREEN. S-1's boundary, on the wire.                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The obvious way to build an operator's view of agent usage was `grant select on agent_messages`
 * plus an operator policy — the pattern `0032` uses for `tenants` — and it would have let a
 * superadmin read **every tenant's conversations**, because a `select` grant is per table and not per
 * column. `0047` refuses that: both reads are `security definer` functions returning counts, and this
 * schema is the same fact stated where a reviewer of the client will see it.
 *
 * A task title, a worklog note and somebody's question are a workspace's contents. A token count is
 * not.
 *
 * ⚠️ **`inferenceGeos` IS here, deliberately, and it is not workspace content.** *"Does a tenant's
 * data leave the country?"* is R-27's open question and a contractual one before it is technical;
 * `agent_messages.inference_geo` records the answer per turn (0043) so that an operator can be shown
 * it rather than told about it.
 */
exports.operatorAgentSpendRowSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    tenantName: zod_1.z.string(),
    workspaceId: zod_1.z.string(),
    workspaceName: zod_1.z.string(),
    /**
     * ⚠️ **Questions and calls are different numbers, and the gap between them is the interesting
     * one.** One question can cost four API calls when the model reads three times before answering, so
     * *"expensive because people ask a lot"* and *"expensive because each answer works hard"* are
     * distinguishable here and nowhere else.
     */
    questions: zod_1.z.number().int().nonnegative(),
    calls: zod_1.z.number().int().nonnegative(),
    inputTokens: zod_1.z.number().int().nonnegative(),
    outputTokens: zod_1.z.number().int().nonnegative(),
    /** ⚠️ The cache's own evidence, and NOT what the cap counts — `0047` argues the difference. */
    cacheReadTokens: zod_1.z.number().int().nonnegative(),
    cacheWriteTokens: zod_1.z.number().int().nonnegative(),
    lastTurnAt: zod_1.z.string().nullable(),
    inferenceGeos: zod_1.z.array(zod_1.z.string()),
});
/**
 * ⚠️ **The window is on the RESPONSE, not only in the request.** A screen that asked for thirty days
 * and rendered whatever came back would have no way to notice a server that answered with seven — and
 * *"the agent cost this much"* over the wrong period is a wrong number rather than a missing one.
 *
 * ⚠️ **`cap` is the deployment's ceiling per workspace per day, or `null` when there is none.** It
 * rides here so the screen can say *"this workspace is at its limit"* rather than making an operator
 * compare a figure against a variable they would have to go and read.
 */
exports.operatorAgentSpendResponseSchema = zod_1.z.object({
    from: zod_1.z.string(),
    to: zod_1.z.string(),
    days: zod_1.z.number().int().positive(),
    cap: zod_1.z.number().int().nonnegative().nullable(),
    rows: zod_1.z.array(exports.operatorAgentSpendRowSchema),
});
/* ── C51.4 · what the programs did ───────────────────────────────────────────────────────────── */
/**
 * One workspace's MCP and token-API traffic.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE SAME BOUNDARY AS AGENT SPEND, AND IT HAD TO BE RE-ARGUED BECAUSE │
 * │ THE OBVIOUS COLUMN HERE IS A TOKEN'S NAME.                                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A personal access token carries a label somebody typed — *"Bence's laptop"*, *"nightly export"* —
 * and it is the first thing anybody would put on a screen about tokens. It is also a tenant's own
 * words, which is exactly what S-1 keeps out of this console. So the credential appears here only as
 * a **count of distinct ones**, and the unit of the row is the workspace.
 *
 * ⚠️⚠️ **REQUESTS AND WRITES COME FROM DIFFERENT TABLES BECAUSE NEITHER CAN ANSWER BOTH.** Reads are
 * not audited — fourteen of this server's twenty-four tools never write anything — so `audit_log`
 * undercounts MCP traffic by an order of magnitude. And the daily counter has no idea whether a
 * request changed anything. `0123`'s `mcp_usage` joins the two.
 *
 * ⚠️ **`apiRequests` is here beside `mcpRequests` and is not noise.** The same credential opens both
 * doors (R-39), so *"this tenant has scripts"* and *"this tenant has chat clients"* are different
 * facts about the same tokens, and an operator looking at one without the other would misread a
 * nightly export as a runaway assistant.
 */
exports.operatorMcpUsageRowSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    tenantName: zod_1.z.string(),
    workspaceId: zod_1.z.string(),
    workspaceName: zod_1.z.string(),
    mcpRequests: zod_1.z.number().int().nonnegative(),
    apiRequests: zod_1.z.number().int().nonnegative(),
    /** How many distinct credentials were used at all. ⚠️ Never which, and never their names. */
    tokensUsed: zod_1.z.number().int().nonnegative(),
    /**
     * ⚠️ **Audited mutations whose channel is `mcp`** — which, because of R-25's gate, means *a
     * program proposed it and a person confirmed it in Forecastify*. There is no such thing here as a
     * write a chat client performed on its own, and the column is worth having precisely because that
     * invariant is otherwise invisible.
     */
    mcpWrites: zod_1.z.number().int().nonnegative(),
    /** ⚠️ A UTC calendar day, not an instant: the counter's bucket, compared across time zones. */
    lastRequestDay: zod_1.z.string().nullable(),
});
/**
 * ⚠️ **The window is on the RESPONSE**, for `operatorAgentSpendResponseSchema`'s reason: a screen
 * that asked for ninety days and rendered whatever came back could not notice a server that answered
 * with thirty, and traffic over the wrong period is a wrong number rather than a missing one.
 */
exports.operatorMcpUsageResponseSchema = zod_1.z.object({
    from: zod_1.z.string(),
    to: zod_1.z.string(),
    days: zod_1.z.number().int().positive(),
    rows: zod_1.z.array(exports.operatorMcpUsageRowSchema),
});
/**
 * How many days of spend a screen may ask for.
 *
 * ⚠️ **1 is *today*, and it is the same window the cap uses** — the calendar day in UTC, so an
 * operator looking at a tenant that is being refused sees the figure the refusal was made from.
 */
exports.OPERATOR_SPEND_WINDOWS = [1, 7, 30, 90];
exports.CONTROL_PANEL_PATHS = {
    signIn: '/control-panel/sign-in',
    /* 2 Nov 2026 · the password step's check — answers, issues nothing. */
    signInPassword: '/control-panel/sign-in/password',
    signOut: '/control-panel/sign-out',
    enrol: '/control-panel/enrol',
    enrolVerify: '/control-panel/enrol/verify',
    /* 0179 · an invitation's link. The token is in the PATH, as the product's `/invite/` has it. */
    join: (token) => `/control-panel/join/${encodeURIComponent(token)}`,
    joinBegin: (token) => `/control-panel/join/${encodeURIComponent(token)}/begin`,
    joinVerify: (token) => `/control-panel/join/${encodeURIComponent(token)}/verify`,
    me: '/control-panel/me',
    tenants: '/control-panel/tenants',
    /* ── C10 ────────────────────────────────────────────────────────────── */
    /**
     * ⚠️ **A sibling of `tenants`, not a `POST` to it.** `POST /tenants` is the product's self-serve
     * sign-up and means *"I am starting an organisation"*; this means *"somebody else is". They
     * create the same rows and they are not the same operation, and one address with two meanings is
     * how the wrong one gets called.
     */
    inviteTenant: '/control-panel/tenants/invite',
    /**
     * ⚠️ `POST`, and the address is the SERVER's — the contact `tenant_counts` resolves — never a
     * body field. An endpoint that mailed a reset link to an address it was handed would be a way to
     * send one to anybody.
     */
    contactPasswordReset: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/contact/password-reset`,
    resendTenantInvitation: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/invite/resend`,
    /** `/control-panel/tenants/:id`, with the id filled in. */
    tenant: (id) => `/control-panel/tenants/${encodeURIComponent(id)}`,
    deleteTenant: (id) => `/control-panel/tenants/${encodeURIComponent(id)}`,
    suspend: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/suspend`,
    activate: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/activate`,
    /* ── S6 ──────────────────────────────────────────────────────────────────────────────────── */
    plans: '/control-panel/plans',
    /* ── C27.5 ───────────────────────────────────────────────────────────────────────────────── */
    /** ⚠️ `?days=` is one of `OPERATOR_SPEND_WINDOWS`; anything else is clamped rather than refused. */
    agentSpend: (days) => `/control-panel/agent-spend?days=${String(days)}`,
    /* ── C51.4 ───────────────────────────────────────────────────────────────────────────────── */
    /** ⚠️ `?days=` is one of `OPERATOR_SPEND_WINDOWS`; anything else is clamped rather than refused. */
    mcpUsage: (days) => `/control-panel/mcp-usage?days=${String(days)}`,
    plan: (id) => `/control-panel/plans/${encodeURIComponent(id)}`,
    assignPlan: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/plan`,
    seatLimit: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/seat-limit`,
    flags: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/flags`,
    /**
     * One flag on one tenant. `PUT` sets an override, `DELETE` clears it — the same address for both,
     * because "what this tenant thinks about this flag" is one resource with two verbs.
     */
    flag: (id, key) => `/control-panel/tenants/${encodeURIComponent(id)}/flags/${encodeURIComponent(key)}`,
    /* ── S7 ──────────────────────────────────────────────────────────────────────────────────── */
    audit: '/control-panel/audit',
    /** BRS §3.11.2's extract: the recent rows about one tenant, unpaged. */
    tenantAudit: (id) => `/control-panel/tenants/${encodeURIComponent(id)}/audit`,
    /**
     * ⚠️ **The two rollups are one address, because they are one screen** — see
     * `operatorOperationsResponseSchema`. The failures list is separate because it PAGES, and a paged
     * collection inside a summary response would have to carry its own cursor in a field the summary
     * has no other use for.
     */
    operations: '/control-panel/operations',
    jobFailures: '/control-panel/operations/failures',
    /** ⚠️ `0178` · `?state=` is one of `OPERATOR_JOB_STATES`, absent for all; `?offset=` pages. */
    jobs: (state, offset = 0) => {
        const query = new URLSearchParams();
        if (state !== null)
            query.set('state', state);
        if (offset > 0)
            query.set('offset', String(offset));
        const suffix = query.toString();
        return `/control-panel/operations/jobs${suffix === '' ? '' : `?${suffix}`}`;
    },
    requeueJob: (id) => `/control-panel/jobs/${encodeURIComponent(id)}/requeue`,
    runChore: (key) => `/control-panel/chores/${encodeURIComponent(key)}/run`,
    /* ── C8 ───────────────────────────────────────────────────────────────────────── */
    /** `GET` the roster, `POST` an invitation. */
    operators: '/control-panel/operators',
    /** ⚠️ `DELETE` only. There is no operator detail screen, so no `GET` on one row. */
    operator: (id) => `/control-panel/operators/${encodeURIComponent(id)}`,
    suspendOperator: (id) => `/control-panel/operators/${encodeURIComponent(id)}/suspend`,
    activateOperator: (id) => `/control-panel/operators/${encodeURIComponent(id)}/activate`,
    /** ⚠️ `POST`, because it SENDS something. A `GET` that mails a link is a link mailed by a crawler. */
    operatorPasswordReset: (id) => `/control-panel/operators/${encodeURIComponent(id)}/password-reset`,
};
//# sourceMappingURL=contract.js.map